import { useQueryClient } from '@tanstack/react-query';
import { DragEvent, useEffect, useState } from 'react';

import { getFile } from '../shared/api/endpoints/files';
import { FILE_URL_FRESH_MS, useFileQuery } from '../shared/api/queries/files';
import { useFeatureFlags } from '../shared/constants/featureFlags';

export const useOpenS3File = () => {
    const queryClient = useQueryClient();
    const { isSafeEmailViewsEnabled } = useFeatureFlags();
    const [fileId, setFileId] = useState('');

    const { data: fileData, isFetching } = useFileQuery({
        fileId,
    });

    useEffect(() => {
        if (fileData) {
            setFileId('');
            if (isSafeEmailViewsEnabled) {
                window.open(fileData, '_blank', 'noopener,noreferrer');
            } else {
                window.open(fileData, '_blank');
            }
        }
    }, [fileData, isFetching, isSafeEmailViewsEnabled]);

    const handleFileClick = (fileId: string) => {
        setFileId(fileId);
    };

    const prefetchFileUrl = (fileId: string) => {
        void queryClient.prefetchQuery({
            queryKey: ['file', fileId],
            queryFn: () => getFile(fileId),
            staleTime: FILE_URL_FRESH_MS,
        });
    };

    const getFreshFileUrl = (fileId: string) => {
        const state = queryClient.getQueryState<string>(['file', fileId]);
        if (!state?.data || Date.now() - state.dataUpdatedAt > FILE_URL_FRESH_MS) return null;
        return state.data;
    };

    // dragstart is synchronous, so the URL is fetched ahead of time on hover, focus, and mousedown.
    const getFileDragProps = (fileId: string, filename: string) => ({
        draggable: true,
        onMouseEnter: () => prefetchFileUrl(fileId),
        onMouseDown: () => prefetchFileUrl(fileId),
        onFocus: () => prefetchFileUrl(fileId),
        onDragStart: (e: DragEvent) => {
            const url = getFreshFileUrl(fileId);
            if (!url) return;
            e.dataTransfer.effectAllowed = 'copy';
            // Chromium's DownloadURL format is "mime:filename:url", so a colon in the name would break it.
            e.dataTransfer.setData(
                'DownloadURL',
                `application/octet-stream:${filename.replace(/:/g, '_')}:${url}`,
            );
            e.dataTransfer.setData('text/uri-list', url);
        },
    });

    return {
        handleFileClick,
        getFileDragProps,
    };
};

export type FileDragProps = ReturnType<ReturnType<typeof useOpenS3File>['getFileDragProps']>;
